import { JSXElementConstructor, Key, lazy, ReactElement } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { usePepwiseAuthController } from 'Context/contexts/AuthContext';
import PrivateRoute from 'utils/privateRoute';
import MLOBSDashboard from 'components/MachineLearning/dashboard';
import ModelDetails from 'components/MachineLearning/models/modelDetails';
import controlTestDashboard from 'components/ControlsCompliance/controlTestDashboard';
import JobRoutes from './Job';
import MasterSystemRoutes from './MasterSystem';
import PlatformCostObsRoutes from './PlatformCostObs';
import DataGrowthRoutes from './DataGrowth';
import IBPCapabilitiesRoutes from './IBPCapabilities';
import DataEstateRoutes from './DataEstate';
import DandAIOpsRoutes from './D&AIOps';
import FinOpsRoutes from './Finops';
import credManagementRoutes from './credential';
import DataPipelineRoutes from './DataPipeline';
import TableauObsRoutes from './TableauObs';
import PrestoObsRoutes from './PrestoObs';
import DatabricksObsRoute from './DatabricksObs';
import DashboardRoutes from './Dashboard';
import DataManagementRoutes from './DataManagement';
import PlatformHealthObsRoutes from './PlatformHealthObs';
import PowerBIObsRoutes from './PowerBIObs';
import VerificationValidationRoutes from './VerificationValidation';
import MDIPCapabilitiesRoutes from './MDIP'
import { GrafanaDashboardAnalytics, GrafanaDashboardIframe } from '../components/framework/grafanaDashboard';
import ProgramExecutiveLanding from 'components/LandingPage/ProgramExeLanding';
import FinanceOpsLanding from 'components/LandingPage/FinanceOpsLanding';
import PlatFormCostObservabilityLanding from 'components/LandingPage/PlatformLanding';
import DataopsLanding from 'components/LandingPage/DataOpsLanding';
import AlertKpiLanding from 'components/LandingPage/AlertAndKpiLanding';
import AlertKpiRoutes from './AlertKpi';
import MonthlyXOpsLanding from 'components/LandingPage/MonthlyXOpsLanding';
import DatabricksADBRoutes from './DatabricksADB'
import EnterprisePlatformRoutes from './EnterprisePlatform';
import PepWiseFeedbackForm from 'Pages/feedback/PepwiseFeedbackForm';
import DemopepwiseV2DashboardRoutes from './PepwiseV2DemoDashboard';
import ProductCatalogObsRoutes from './ProductCatalogObs';
import ProductSuccessObsRoutes from './ProductSuccessObs';
import UnifiedAlertsRoutes from './UnifiedAlerts';
import PipelineRunStatusRoutes from './PipelineRunStatus';
import CrossFlowNavigatorRoutes from './CrossFlowNavigator';
import XOpsIntelligence from 'components/XOpsIntelligence';
import XOpsIntelligenceHost from 'components/XOpsIntelligence/XOpsIntelligenceHost';
import DataAssetIntelligence from 'components/DataAssetIntelligence';

// Lazy load components
const LandingPage = lazy(() => import('components/LandingPage/LandingPage'));

const routes = [
	//Landing page
	{
		key: 'landing',
		route: '/LandingPage',
		component: (
			<PrivateRoute
				Component={LandingPage}
				restrictedRoles={['D&AI Executive User', 'Program Executive']}
			/>
		)
	},
	...DashboardRoutes,
	...DatabricksObsRoute,
	...DataEstateRoutes,
	...DandAIOpsRoutes,
	...DataGrowthRoutes,
	...DataManagementRoutes,
	...DataPipelineRoutes,
	...FinOpsRoutes,
	...credManagementRoutes,
	...IBPCapabilitiesRoutes,
	...JobRoutes,
	...MasterSystemRoutes,
	...PlatformCostObsRoutes,
	...PlatformHealthObsRoutes,
	...PowerBIObsRoutes,
	...PrestoObsRoutes,
	...TableauObsRoutes,
	...VerificationValidationRoutes,
	...MDIPCapabilitiesRoutes,
	...AlertKpiRoutes,
	...DatabricksADBRoutes,
	...EnterprisePlatformRoutes,
	...DemopepwiseV2DashboardRoutes,
	...ProductCatalogObsRoutes,
	...ProductSuccessObsRoutes,
	...UnifiedAlertsRoutes,
	...PipelineRunStatusRoutes,
	...CrossFlowNavigatorRoutes,
	//Control and Complaiance observability
	{
		key: 'ctrl_comp_dash',
		route: '/controlsCompliance/dashboard',
		component: (
			<PrivateRoute
				Component={controlTestDashboard}
				restrictedRoles={['Data Operations Admin', 'Data Operations User', 'D&AI Executive User', 'Program Executive']}
			/>
		)
	},
	//Machine learning
	{
		key: 'mach_lrn_dash',
		route: '/machineLearning/dashboard',
		component: (
			<PrivateRoute
				Component={MLOBSDashboard}
				restrictedRoles={['Data Operations Admin', 'Data Operations User', 'D&AI Executive User', 'Program Executive']}
			/>
		)
	},
	{
		key: 'mach_lrn_modl_id',
		route: 'machineLearning/models/:modelId',
		component: (
			<PrivateRoute
				Component={ModelDetails}
				restrictedRoles={['Data Operations Admin', 'Data Operations User', 'D&AI Executive User', 'Program Executive']}
			/>
		)
	},
	{
		key: 'grafana_dashboard_poc_analytics',
		route: 'grafanaDashboard/poc/analytics',
		component: (
			<PrivateRoute
				Component={GrafanaDashboardAnalytics}
				restrictedRoles={['Data Operations Admin', 'Data Operations User', 'D&AI Executive User', 'Program Executive']}
			/>
		)
	},
	{
		key: 'grafana_dashboard_poc_iframe',
		route: 'grafanaDashboard/poc/iframe',
		component: (
			<PrivateRoute
				Component={GrafanaDashboardIframe}
				restrictedRoles={['Data Operations Admin', 'Data Operations User', 'D&AI Executive User', 'Program Executive', 'Platform Operations Admin/SRE']}
			/>
		)
	},
	{
		key: 'dashboard_programExecutive',
		route: 'dashboard/programExecutiveLanding',
		component: (
			<PrivateRoute
				Component={ProgramExecutiveLanding}
				restrictedRoles={['Data Operations Admin', 'Data Operations User', 'D&AI Executive User', 'Platform Operations Admin/SRE']}
			/>
		)
	},
	{
		key: 'dashboard_financeOpsLanding',
		route: 'dashboard/financeOpsLanding',
		component: (
			<PrivateRoute
				Component={FinanceOpsLanding}
				restrictedRoles={['Data Operations Admin', 'Data Operations User', 'D&AI Executive User','Platform Operations Admin/SRE']}
			/>
		)
	},
	{
		key: 'dashboard_platformCostLanding',
		route: '/dashboard/platformCostLanding',
		component: (
			<PrivateRoute
				Component={PlatFormCostObservabilityLanding}
				restrictedRoles={['Data Operations Admin', 'Data Operations User', 'D&AI Executive User', 'Program Executive']}
			/>
		)
	},
	{
		key: 'dpipe_landing_page',
		route: '/dashboard/dataOpsLanding', 
		component: (
			<PrivateRoute
				Component={DataopsLanding} 
				restrictedRoles={['Platform Operations Admin/SRE', 'D&AI Executive User', 'Program Executive']}
			/>
		)
	},
	// {
	// 	key: 'dandAIOps_landing_page',
	// 	route: '/dashboard/D&AIOpsLanding',
	// 	component: (
	// 		<PrivateRoute
	// 			Component={DandAIOpsLanding} 
	// 			restrictedRoles={['Data Operations Admin', 'Data Operations User', 'Platform Operations Admin/SRE', 'D&AI Executive User', 'Program Executive']}
	// 		/>
	// 	)
	// },
	{
		key: 'alertkpi_landing_page',
		route: '/dashboard/alertKpiLanding', 
		component: (
			<PrivateRoute
				Component={AlertKpiLanding} 
				restrictedRoles={['Platform Operations Admin/SRE', 'D&AI Executive User', 'Program Executive']}
			/>
		)
	},
	{
		key: 'mxops_landing_page',
		route: '/dashboard/monthlyXOpsLanding', 
		component: (
			<PrivateRoute
				Component={MonthlyXOpsLanding} 
				restrictedRoles={['Data Operations Admin', 'Data Operations User', 'D&AI Executive User', 'Program Executive']}
			/>
		)
	},
	{
		key: 'pepwise_v2_feedback',
		route: '/pepwisev2/feedback',
		component: (
			<PrivateRoute
				Component={PepWiseFeedbackForm}
				restrictedRoles={['Data Operations Admin', 'Data Operations User', 'D&AI Executive User', 'Program Executive']}
			/>
		)
	},
	{
		key: 'xops_intelligence',
		route: '/dashboard/xops-intelligence',
		component: (
			<PrivateRoute
				Component={XOpsIntelligence}
				restrictedRoles={['Data Operations Admin', 'Data Operations User', 'D&AI Executive User', 'Program Executive']}
			/>
		)
	},
	{
		key: 'xops_intelligence_host',
		route: '/dashboard/xops-intelligence/:slug',
		component: (
			<PrivateRoute
				Component={XOpsIntelligenceHost}
				restrictedRoles={['Data Operations Admin', 'Data Operations User', 'D&AI Executive User', 'Program Executive']}
			/>
		)
	},
	{
		// Wildcard: the dashboard owns its six sub-screens as nested routes.
		key: 'data_assets_intelligence',
		route: '/dashboard/xops-intelligence/data-assets-intelligence/*',
		component: (
			<PrivateRoute
				Component={DataAssetIntelligence}
				restrictedRoles={['Data Operations Admin', 'Data Operations User', 'D&AI Executive User', 'Program Executive']}
			/>
		)
	},
];

const getRoutes = (allRoutes: any[]): any =>
	allRoutes.map(
		(route: {
			default: any;
			route: string;
			component: ReactElement<any, string | JSXElementConstructor<any>>;
			key: Key;
		}): object => {
			return <Route path={route.route} element={route.component} key={route.key} />;
		}
	);

const AppRoutes = () => {
	const [controller] = usePepwiseAuthController();
	const { isAuthenticated, accessToken } = controller;

	const roleName = (accessToken?.role?.name as string) ?? 'default';
	const rolePaths: { [key: string]: string } = {
		'Data Operations Admin': '/LandingPage',
		'Data Operations User': '/LandingPage',
		'D&AI Executive User': '/dataestate/executive-summary',
		'Program Executive': '/ibp/executive',
		default: '/LandingPage'
	};

	const defaultRoutePath =
		!isAuthenticated || !rolePaths.hasOwnProperty(roleName) ? '/LandingPage' : rolePaths[roleName];

	return (
		<Routes>
			{getRoutes(routes)}
			<Route path="*" element={<Navigate to={defaultRoutePath} replace />} />
		</Routes>
	);
};

export default AppRoutes;
